import { showChatWidget } from "@faire/help-center-self-serve/chat/chatWidget";
import { useChatAuthorization } from "@faire/help-center-self-serve/chat/hooks/useChatAuthorization";
import { BRAND_TYPE } from "@faire/help-center-utils/consts/BRAND_TYPE";
import { RETAILER_TYPE } from "@faire/help-center-utils/consts/RETAILER_TYPE";
import { browserLevelLanguage } from "@faire/help-center-utils/serialized-data/getLocale";
import { getRetailer } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getRetailerOrBrandCountry } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandCountry";
import { getUserToken } from "@faire/help-center-utils/serialized-data/getUser";
import { getSettingHelpCenterChatAvailableLocales } from "@faire/help-center-utils/settings/getSettingHelpCenterChatAvailableLocales";
import { getSettingHelpCenterLanguageFieldEnabled } from "@faire/help-center-utils/settings/getSettingHelpCenterLanguageFieldEnabled";
import { DEFAULT_LOCALE_KEY } from "@faire/web--source/common/consts/DEFAULT_LOCALE";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { trackHelpCenterChatProvidedView } from "@faire/web-api--source/events/helpCenter/view/chatProvided";
import { IFaireMetadata } from "@faire/web-api--source/sunco/IFaireMetadata";
import { Dispatch, SetStateAction, useCallback, useEffect } from "react";

import { getAllZenDeskTags } from "../utils/zenDeskTags";

import { useAIChatbotAccess } from "./useAIChatbotAccess";
import { useIssueType } from "./useIssueType";
import { useOrderToken } from "./useOrderToken";

interface IUseChat {
  setShowChat: Dispatch<SetStateAction<boolean>>;
  loadingChat: boolean;
  setLoadingChat: Dispatch<SetStateAction<boolean>>;
  hasChatAccess: boolean;
  chatBtnEnabled: boolean;
}

export const useChat = (): IUseChat => {
  const { issueType } = useIssueType();
  const [showChat, setShowChat] = useState(
    issueType.includes("FaireDirectWidgetChat")
  );
  const [loadingChat, setLoadingChat] = useState<boolean>(false);
  const { orderToken } = useOrderToken();
  const { strictLocalize } = useStrictLocalization();
  const { hasAIChatbotAccess } = useAIChatbotAccess();
  const {
    data: jwtResponse,
    isLoading,
    chatBtnEnabled,
    hasChatAccess,
  } = useChatAuthorization();

  /**
   * This is the place where we show the chat widget.
   */
  useEffect(() => {
    setLoadingChat(true);
    if (isLoading) {
      return;
    }
    if (hasAIChatbotAccess) {
      setLoadingChat(false);
      return;
    }

    if (showChat && jwtResponse) {
      const metaData: IFaireMetadata = getFaireMetadata(
        jwtResponse.metadata,
        orderToken,
        issueType
      );
      showChatWidget(jwtResponse, strictLocalize, metaData).then(() =>
        setLoadingChat(false)
      );
    }
    setLoadingChat(false);
  }, [
    showChat,
    jwtResponse,
    isLoading,
    orderToken,
    issueType,
    hasAIChatbotAccess,
    strictLocalize,
  ]);

  const trackChatView = useCallback(() => {
    const userToken = getUserToken() ?? "";
    trackHelpCenterChatProvidedView(userToken, userToken, issueType);
  }, [issueType]);

  useEffect(() => {
    if (hasChatAccess && chatBtnEnabled) {
      trackChatView();
    }
  }, [chatBtnEnabled, hasChatAccess, trackChatView]);

  return {
    setShowChat,
    loadingChat,
    setLoadingChat,
    hasChatAccess,
    chatBtnEnabled,
  };
};

const getFaireMetadata = (
  metaData: IFaireMetadata | undefined,
  orderToken: string | undefined,
  issueType: string
) => {
  const retailer = getRetailer();
  const customerType = retailer?.token ? RETAILER_TYPE : BRAND_TYPE;
  const countryIso = getRetailerOrBrandCountry() ?? "";
  const tags = getAllZenDeskTags(countryIso, customerType, issueType);
  const chatAvailableLanguages = getSettingHelpCenterChatAvailableLocales().map(
    (locale) => locale.language
  );
  const language = chatAvailableLanguages.includes(browserLevelLanguage())
    ? browserLevelLanguage()
    : DEFAULT_LOCALE_KEY.language;

  if (getSettingHelpCenterLanguageFieldEnabled()) {
    return IFaireMetadata.build({
      ...metaData,
      tags,
      brand_order_token: orderToken,
      issue_type: issueType,
      language,
    });
  } else {
    return IFaireMetadata.build({
      ...metaData,
      tags,
      brand_order_token: orderToken,
      issue_type: issueType,
    });
  }
};
